import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Coins, Diamond, SignOut, CaretDown, Check, List, House, SquaresFour, BookOpen, CaretRight, User, SpeakerHigh, SpeakerSlash, Certificate, ArrowSquareOut, DeviceMobile, Heart, ChatCircleText, ChatCircleDots, Fingerprint, BellRinging, Sparkle } from "@phosphor-icons/react";
import { pushSupported, getPushSubscription, enablePush, disablePush } from "@/lib/push";
import { fetchCoachEnabled } from "@/components/CoachFab";
import { getDeferredPrompt, clearDeferredPrompt, isStandalone, isIos, checkInstalled, installInstructions, recordInstall, isMobile } from "@/lib/installPrompt";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { usePillars } from "@/contexts/PillarsContext";
import { useNavigate, useLocation } from "react-router-dom";
import { roleLabel } from "@/lib/roles";
import { useI18n } from "@/contexts/I18nContext";
import { NotificationsBell } from "@/components/NotificationsBell";
import { InboxButton } from "@/components/InboxButton";
import { SosButton } from "@/components/SosButton";
import { FeedbackButton } from "@/components/FeedbackButton";
import { LevelRing } from "@/components/LevelRing";
import { VersionStamp } from "@/components/VersionStamp";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Smoothly counts the header coin balance up whenever it increases, with a bump
// and a one-shot "+N" gain tag.
export function compactCoins(n) {
  if (n < 1000) return String(n);
  if (n < 1000000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}m`;
}

function useCoinCounter(target) {
  const [display, setDisplay] = useState(target);
  const [bump, setBump] = useState(false);
  const [gain, setGain] = useState(null); // { amount, key }
  const prev = useRef(target);
  useEffect(() => {
    const from = prev.current;
    prev.current = target;
    if (target === from) return;
    if (target < from) { setDisplay(target); return; } // no animation on decrease/reset
    setBump(true);
    setGain({ amount: target - from, key: Date.now() });
    const bumpTimer = setTimeout(() => setBump(false), 650);
    const gainTimer = setTimeout(() => setGain(null), 1600);
    const start = performance.now();
    const dur = 900;
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); clearTimeout(bumpTimer); clearTimeout(gainTimer); };
  }, [target]);
  return { display, bump, gain };
}

const TAB_LABELS = {
  users: "School Users", personal: "Personal Accounts", tokens: "Seats", library: "Library",
  courses: "Courses", roster: "Roster", security: "Security", pillars: "Pillars",
  branding: "Branding", translations: "Translations", schools: "School Boards/Schools", "school-logo": "School Logo",
  finance: "Financials", invoices: "Money", certificates: "Certificates", renewals: "Renewals",
  matrix: "Lesson Matrix", messaging: "Messaging", instructors: "Instructors", audit: "Audit Log",
};

function Breadcrumb({ nav }) {
  const loc = useLocation();
  const { t } = useI18n();
  const crumbs = [{ label: "Dashboard", to: "/dashboard" }];
  if (loc.pathname.startsWith("/admin")) {
    crumbs.push({ label: "Operations", to: "/admin" });
    const tab = new URLSearchParams(loc.search).get("tab");
    if (tab) crumbs.push({ label: TAB_LABELS[tab] || tab, to: null });
  } else if (loc.pathname.startsWith("/catalog")) {
    crumbs.push({ label: "Course Catalog", to: null });
  } else if (loc.pathname.startsWith("/course/")) {
    crumbs.push({ label: "Course", to: null });
  } else if (loc.pathname.startsWith("/certificate")) {
    crumbs.push({ label: "Certificate", to: null });
  }
  return (
    <div className="max-w-6xl mx-auto px-5 pb-2 -mt-1 flex items-center gap-1 text-xs text-[#000000]" data-testid="header-breadcrumb">
      {crumbs.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <CaretRight size={11} weight="bold" className="text-[#000000]" />}
          {c.to && i < crumbs.length - 1 ? (
            <button className="btn-ghost hover:text-[#000000] hover:underline underline-offset-2 font-medium" onClick={() => nav(c.to)}>{t(c.label)}</button>
          ) : (
            <span className="font-bold text-[#000000]">{t(c.label)}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function HeaderBar() {
  const { t, lang, setLang, languages } = useI18n();
  const { user, logout, switchRole } = useAuth();
  const { settings, settingsLoaded } = usePillars();
  const nav = useNavigate();
  const loc = useLocation();
  const [coinSounds, setCoinSounds] = React.useState(() => localStorage.getItem("coinSoundsOff") !== "1");
  const toggleCoinSounds = () => {
    const next = !coinSounds;
    setCoinSounds(next);
    localStorage.setItem("coinSoundsOff", next ? "0" : "1");
  };
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [coachOk, setCoachOk] = useState(true);
  useEffect(() => { fetchCoachEnabled().then(setCoachOk); }, []);
  useEffect(() => {
    if (!user || !pushSupported()) return;
    getPushSubscription().then((sub) => setPushOn(!!sub && Notification.permission === "granted"));
  }, [user]);
  const toggleAppNotifications = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.success("App reminders turned off on this device");
      } else {
        await enablePush();
        setPushOn(true);
        toast.success("App reminders on — this device will get your streak reminders & alerts");
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't change notification settings");
    } finally { setPushBusy(false); }
  };
  const { display: coinDisplay, bump: coinBump, gain: coinGain } = useCoinCounter(user?.coins_balance ?? user?.xp ?? 0);
  const [switching, setSwitching] = useState(false);
  const [installHelp, setInstallHelp] = useState(null);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [sosOpen, setSosOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [sosEnabled, setSosEnabled] = useState(false);
  const [lessonHelpEnabled, setLessonHelpEnabled] = useState(false);
  useEffect(() => {
    if (!user) { setSosEnabled(false); setLessonHelpEnabled(false); return; }
    api.get("/sos/resources").then(r => setSosEnabled(!!r.data?.sos_enabled)).catch(() => {});
    api.get("/inbox/summary").then(r => setLessonHelpEnabled(!!r.data?.inbox_enabled && !!r.data?.channels?.per_lesson_help)).catch(() => {});
  }, [user?.id]); // eslint-disable-line
  useEffect(() => {
    if (!user) { setCompletedCourses([]); return; }
    api.get("/courses")
      .then(r => setCompletedCourses((r.data || []).filter(c => (c.progress_percent ?? 0) >= 100)))
      .catch(() => setCompletedCourses([]));
  }, [user?.id, user?.completed_lesson_ids?.length]); // eslint-disable-line
  // Gem Shop perk: equipped app theme swaps the accent palette app-wide.
  useEffect(() => {
    document.body.classList.remove("theme-ocean", "theme-ember", "theme-royal");
    if (user?.app_theme) document.body.classList.add(`theme-${user.app_theme}`);
  }, [user?.app_theme]);

  if (!user) return null;

  const onLogout = async () => {
    await logout();
    nav("/", { replace: true });
  };

  const roles = user.roles || [user.role];
  const multiRole = roles.length > 1;
  const onSwitchRole = async (r) => {
    if (r === user.role || switching) return;
    setSwitching(true);
    try {
      await switchRole(r);
      toast.success(`Switched to ${roleLabel(r)}`);
      nav("/dashboard", { replace: true });
    } catch (e) {
      toast.error("Couldn't switch role");
    } finally {
      setSwitching(false);
    }
  };

  const isSuperAdmin = user.role === "super_admin";
  const isSchoolStaff = ["school_board_admin", "school_admin", "teacher"].includes(user.role);
  const isInstructor = ["super_admin", "level2_instructor", "level1_instructor", "team_dev", "team_content"].includes(user.role);
  const showBreadcrumb = isInstructor || isSchoolStaff; // admins & staff only — never students
  const showCatalog = isInstructor || isSchoolStaff || user.role === "public_student";
  const hideGamification = isSuperAdmin || isSchoolStaff;
  const logoUrl = user?.school_logo_url || settings?.logo_url;
  const brandName = user?.school_logo_url
    ? (user?.school_name || "School")
    : (settings?.cover_title || "The Self-Agency Project");

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#DCDEE2]/85 border-b border-[#C4C8CE]">
      <div className="max-w-6xl mx-auto px-3 sm:px-5 py-3 flex items-center justify-between gap-2 sm:gap-4">
        <button
          data-testid="brand-home-button"
          onClick={() => nav("/")}
          className="flex items-center gap-2 bg-transparent border-0 cursor-pointer min-w-0 shrink"
        >
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} data-testid="header-logo-image" className="h-9 w-auto min-w-0 max-w-[120px] sm:max-w-[160px] object-contain" />
          ) : !settingsLoaded ? (
            <div className="w-9 h-9 shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-[#CCFF00] text-[#000000] grid place-items-center font-extrabold text-lg font-display shrink-0">E</div>
          )}
          <span className="brand-text text-xl truncate hidden sm:inline">{brandName}</span>
          <span data-testid="header-user-name" className="text-sm font-semibold truncate hidden sm:inline" style={{ color: user.flair_color || "#52525B" }}>
            · {user.name || user.code}
          </span>
          {user.flair_title && (
            <span data-testid="header-flair-title" className="hidden sm:inline px-2 py-0.5 rounded-full text-[10px] font-display font-extrabold uppercase tracking-wider shrink-0 text-white"
              style={{ background: user.flair_color || "#1F2937" }}>
              {user.flair_title}
            </span>
          )}
        </button>

        <div className="flex flex-wrap items-center justify-end gap-1 sm:gap-3 shrink min-w-0">
          {!loc.pathname.startsWith("/course/") && <SosButton />}
          <InboxButton />
          {!loc.pathname.startsWith("/course/") && <FeedbackButton />}
          <NotificationsBell />
          {!hideGamification && (
            <>
              <LevelRing xp={user.xp || 0} />
              <div data-testid="header-streak" className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-full bg-[#FFFFFF] border border-[#C4C8CE]">
                <Flame size={20} weight="duotone" color="#FF5100" />
                <span className="font-display font-bold text-[#FF5100]">{user.streak || 0}</span>
              </div>
              <motion.div
                data-testid="header-xp"
                className="relative flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-full bg-[#FFFFFF] border border-[#C4C8CE]"
                animate={coinBump ? { scale: [1, 1.18, 1], borderColor: ["#C4C8CE", "#FFC400", "#C4C8CE"] } : {}}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <AnimatePresence>
                  {coinGain && (
                    <motion.span
                      key={coinGain.key}
                      data-testid="header-coin-gain"
                      className="absolute left-1/2 -top-1 -translate-x-1/2 pointer-events-none font-display font-extrabold text-sm text-outline-lime whitespace-nowrap"
                      initial={{ opacity: 0, y: 6, scale: 0.7 }}
                      animate={{ opacity: 1, y: -22, scale: 1 }}
                      exit={{ opacity: 0, y: -34 }}
                      transition={{ duration: 1.4, ease: "easeOut" }}
                    >
                      +{coinGain.amount}
                    </motion.span>
                  )}
                </AnimatePresence>
                <motion.span
                  animate={coinBump ? { rotate: [0, -18, 18, 0] } : {}}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="inline-flex"
                >
                  <Coins size={20} weight="fill" color="#FFC400" className="icon-outline" />
                </motion.span>
                <span className="font-display font-bold text-outline-lime sm:hidden" data-testid="header-coins-compact">{compactCoins(coinDisplay)}</span>
                <span className="font-display font-bold text-outline-lime hidden sm:inline">{coinDisplay}</span>
              </motion.div>
              {["emeralds", "rubies", "diamonds"].map((gem) => {
                const n = user?.[gem] ?? 0;
                if (n <= 0) return null;
                const c = gem === "emeralds" ? "#34D399" : gem === "rubies" ? "#F87171" : "#7DD3FC";
                return (
                  <div key={gem} className={(gem === "diamonds" ? "flex" : "hidden sm:flex") + " items-center gap-0.5 px-1.5 py-1 sm:px-2 sm:py-1.5 rounded-full shrink"}
                    style={{ background: `${c}1f`, border: `1px solid ${c}80` }}
                    title={gem === "emeralds" ? "Emeralds — 20 coins each" : gem === "rubies" ? "Rubies — 5 emeralds each" : "Diamonds — 5 rubies each"}
                    data-testid={`header-${gem}`}>
                    <Diamond size={13} weight="fill" color={c} className="icon-outline shrink-0" />
                    <span className="font-display font-bold text-xs sm:text-sm" style={{ color: c }} data-testid={`header-${gem}-count`}>{compactCoins(n)}</span>
                  </div>
                );
              })}
            </>
          )}
          {isSuperAdmin && (
            <div data-testid="header-admin-badge" className="hidden sm:block px-3 py-1.5 rounded-full bg-[rgba(204,255,0,0.1)] border border-[rgba(163,201,0,0.5)] text-[#000000] text-xs font-display font-bold uppercase tracking-[0.15em]">
              Super Admin
            </div>
          )}
          {multiRole && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="role-switcher-trigger"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#000000] border border-[#C4C8CE] hover:bg-[#F4F4F5] transition-colors cursor-pointer"
                  title="Switch role context"
                >
                  <span className="text-xs font-display font-bold uppercase tracking-[0.12em] max-w-[140px] truncate text-[#000000]">{roleLabel(user.role)}</span>
                  <CaretDown size={14} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white text-[#000000] border-[#C4C8CE] shadow-xl" data-testid="role-switcher-menu">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-[#000000]">Switch context</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {roles.map((r) => (
                  <DropdownMenuItem
                    key={r}
                    data-testid={`role-option-${r}`}
                    onClick={() => onSwitchRole(r)}
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span className="font-medium">{roleLabel(r)}</span>
                    {r === user.role && <Check size={16} weight="bold" className="text-[#000000]" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="header-menu-trigger" className="btn-ghost p-2 rounded-xl border border-[#C4C8CE] bg-white hover:bg-[#F4F4F5] transition-colors" title="Menu" aria-label="Open menu">
                <List size={22} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 bg-white text-[#000000] border-[#C4C8CE] shadow-xl" data-testid="header-menu">
              <DropdownMenuLabel className="flex items-center gap-2">
                <User size={18} weight="duotone" className="shrink-0" />
                <span className="min-w-0">
                  <span className="block font-bold truncate">{user.name || user.code}</span>
                  <span className="block text-[10px] uppercase tracking-[0.12em] text-[#000000] font-normal">{roleLabel(user.role)}</span>
                  {user.pilot_tester && (
                    <span className="inline-block mt-0.5 px-1.5 py-px rounded-full bg-[#FEF3C7] border border-[#F59E0B] text-[#B45309] text-[9px] font-bold tracking-wide" data-testid="pilot-tester-badge">
                      PILOT TESTER
                    </span>
                  )}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-dashboard" className="cursor-pointer gap-2" onClick={() => nav("/dashboard")}>
                <House size={17} weight="duotone" /> {t("Dashboard")}
              </DropdownMenuItem>
              {(isInstructor || isSchoolStaff) && (
                <DropdownMenuItem data-testid="menu-operations" className="cursor-pointer gap-2" onClick={() => nav("/admin")}>
                  <SquaresFour size={17} weight="duotone" /> {t("Operations Panel")}
                </DropdownMenuItem>
              )}
              {(isInstructor || isSchoolStaff) && (
                <DropdownMenuItem data-testid="menu-signin-sandbox" className="cursor-pointer gap-2"
                  onClick={() => window.open("/student?sandbox=1", "_blank")}
                  title="Opens a tab fully severed from your session — sign in/out exactly like a student">
                  <ArrowSquareOut size={17} weight="duotone" /> {t("Student sign-in sandbox")}
                </DropdownMenuItem>
              )}
              {isSuperAdmin && (
                <DropdownMenuItem data-testid="menu-enable-passkey" className="cursor-pointer gap-2"
                  title="Register this device's fingerprint/face unlock for one-tap sign-in"
                  onClick={async () => {
                    try {
                      const { startRegistration } = await import("@simplewebauthn/browser");
                      const { data: opts } = await api.post("/auth/passkey/register/options", { origin: window.location.origin });
                      const { challenge_token } = opts;
                      const cred = await startRegistration({ optionsJSON: opts });
                      await api.post("/auth/passkey/register/verify", { ...cred, challenge_token, origin: window.location.origin });
                      localStorage.setItem("saproject:passkey-enrolled", "1");
                      toast.success("Fingerprint sign-in enabled on this device");
                    } catch (e) {
                      if (e?.name !== "NotAllowedError") toast.error("Couldn't enable fingerprint sign-in on this device");
                    }
                  }}>
                  <Fingerprint size={17} weight="duotone" /> Enable fingerprint sign-in
                </DropdownMenuItem>
              )}
              {showCatalog && (
                <DropdownMenuItem data-testid="menu-catalog" className="cursor-pointer gap-2" onClick={() => nav("/catalog")}>
                  <BookOpen size={17} weight="duotone" /> Course Catalog
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.12em] text-[#000000]">Support</DropdownMenuLabel>
              {coachOk && (
                <DropdownMenuItem data-testid="menu-coach" className="cursor-pointer gap-2" onClick={() => nav("/coach")}>
                  <Sparkle size={17} weight="duotone" color="#B45309" /> Talk to Coach
                </DropdownMenuItem>
              )}
              {sosEnabled && user.role !== "public_student" && (
                <DropdownMenuItem data-testid="menu-helpline" className="cursor-pointer gap-2"
                  onSelect={() => setTimeout(() => setSosOpen(true), 0)}>
                  <Heart size={17} weight="fill" color="#DC2626" /> Help Line Numbers
                </DropdownMenuItem>
              )}
              {lessonHelpEnabled && user.role === "anonymous_token_student" && (
                <DropdownMenuItem data-testid="menu-lesson-help" className="cursor-pointer gap-2"
                  onClick={() => nav("/inbox", { state: { compose: { body: "I need help with a lesson. My question is: " } } })}>
                  <ChatCircleText size={17} weight="duotone" /> Help with a lesson
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="menu-feedback" className="cursor-pointer gap-2"
                onSelect={() => setTimeout(() => setFeedbackOpen(true), 0)}>
                <ChatCircleDots size={17} weight="duotone" /> Send feedback to developer
              </DropdownMenuItem>
              {completedCourses.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  {completedCourses.map(c => (
                    <DropdownMenuItem key={c.id} data-testid={`menu-certificate-${c.id}`} className="cursor-pointer gap-2" onClick={() => nav(`/certificate/${c.id}`)}>
                      <Certificate size={17} weight="duotone" color="#B8860B" /> Certificate — <span className="font-bold truncate">{c.title}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              {languages.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.12em] text-[#000000]">{t("Language")}</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="lang-en" className="cursor-pointer gap-2"
                    onSelect={(e) => { e.preventDefault(); setLang("en"); }}>
                    {lang === "en" ? <Check size={15} weight="bold" /> : <span className="w-[15px]" />} English
                  </DropdownMenuItem>
                  {languages.map((l) => (
                    <DropdownMenuItem key={l.lang} data-testid={`lang-${l.lang}`} className="cursor-pointer gap-2"
                      onSelect={(e) => { e.preventDefault(); setLang(l.lang); }}>
                      {lang === l.lang ? <Check size={15} weight="bold" /> : <span className="w-[15px]" />} {l.label || l.lang}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-install-app" className="cursor-pointer gap-2"
                onClick={async () => {
                  if (isStandalone()) { setInstallHelp("standalone"); return; }
                  const deferred = getDeferredPrompt();
                  if (deferred) {
                    deferred.prompt();
                    const { outcome } = await deferred.userChoice;
                    clearDeferredPrompt();
                    if (outcome === "accepted") {
                      localStorage.setItem("saproject:pwa-installed", "1");
                      recordInstall("menu");
                      setInstallHelp("success");
                    }
                    return;
                  }
                  setInstallHelp((await checkInstalled()) ? "installed" : "howto");
                }}>
                <DeviceMobile size={17} weight="duotone" /> {t("Install app")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="menu-coin-sounds"
                className="cursor-pointer gap-2"
                onSelect={(e) => { e.preventDefault(); toggleCoinSounds(); }}
              >
                {coinSounds ? <SpeakerHigh size={17} weight="duotone" /> : <SpeakerSlash size={17} weight="duotone" />}
                {t("Coin sounds")}: <span className="font-bold">{coinSounds ? t("On") : t("Off")}</span>
              </DropdownMenuItem>
              {pushSupported() && (
                <DropdownMenuItem
                  data-testid="menu-app-notifications"
                  className="cursor-pointer gap-2"
                  onSelect={(e) => { e.preventDefault(); toggleAppNotifications(); }}
                >
                  {pushBusy ? <BellRinging size={17} weight="duotone" className="animate-pulse" /> : <BellRinging size={17} weight={pushOn ? "fill" : "duotone"} />}
                  {t("App reminders")}: <span className="font-bold" data-testid="app-notifications-state">{pushBusy ? "…" : pushOn ? t("On") : t("Off")}</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-logout" className="cursor-pointer gap-2 text-[#B91C1C] focus:text-[#B91C1C]" onClick={onLogout}>
                <SignOut size={17} weight="duotone" /> {t("Log out")}
              </DropdownMenuItem>
              {user?.role === "public_student" && (
                <DropdownMenuItem data-testid="menu-delete-account" className="cursor-pointer gap-2 text-[#000000] focus:text-[#B91C1C]"
                  onClick={async () => {
                    if (!window.confirm("Delete your account? Your name, email and login are permanently removed and course access ends. This cannot be undone.")) return;
                    if (!window.confirm("Last check — really delete your account forever?")) return;
                    try {
                      await api.post("/account/delete", { confirm: true });
                      await logout();
                      window.location.href = "/";
                    } catch { /* stay logged in on failure */ }
                  }}>
                  <User size={17} weight="duotone" /> Delete my account
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <VersionStamp />
            </DropdownMenuContent>
          </DropdownMenu>
          <SosButton variant="menu" open={sosOpen} onOpenChange={setSosOpen} />
          <FeedbackButton variant="menu" open={feedbackOpen} onOpenChange={setFeedbackOpen} />
        </div>
      </div>
      <Dialog open={!!installHelp} onOpenChange={(o) => !o && setInstallHelp(null)}>
        <DialogContent className="max-w-md" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle>
              {installHelp === "howto" ? t("Install app")
                : installHelp === "success" ? t("Installed!")
                : t("App already installed")}
            </DialogTitle>
          </DialogHeader>
          {installHelp === "howto" && (
            <div className="text-sm space-y-3">
              <p>{installInstructions()}</p>
              <p className="text-[#52525B] text-xs">After installing, the app opens in its own window with its own icon — no browser needed.</p>
            </div>
          )}
          {installHelp === "success" && (
            <div className="text-sm space-y-3" data-testid="install-success">
              <p>The app is installing — it'll open in its own window with its own icon in a moment.</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><b>Windows:</b> the icon is in your Start Menu — right-click it on the taskbar → <i>Pin to taskbar</i> to keep it handy.</li>
                <li><b>Mac:</b> find it in Launchpad and drag it to the Dock.</li>
                <li><b>Phone:</b> the icon is on your home screen or app drawer.</li>
              </ul>
            </div>
          )}
          {(installHelp === "installed" || installHelp === "standalone") && (
            <div className="text-sm space-y-3">
              <p>{installHelp === "standalone"
                ? "You're using the installed app right now — nice. It already has its own icon on this device."
                : "The app is already installed on this device."}</p>
              <div>
                <div className="font-semibold mb-1">To put a shortcut on your desktop:</div>
                <ul className="list-disc pl-5 space-y-1">
                  <li><b>Windows:</b> open the Start menu, search "Self-Agency", then right-click it → <i>Pin to taskbar</i>, or drag it onto the desktop.</li>
                  <li><b>Mac:</b> it's in Launchpad and your Applications/Chrome Apps folder — drag "Self-Agency" to the Dock or desktop.</li>
                  <li><b>Phone/tablet:</b> the icon is on your home screen or app drawer already.</li>
                </ul>
              </div>
            </div>
          )}
          {!isMobile() && installHelp !== "success" && (
            <div className="flex items-center gap-3 rounded-lg border border-[#C4C8CE] p-3" data-testid="install-dialog-qr">
              <div className="bg-white p-1.5 rounded shrink-0">
                <QRCodeSVG value={`${window.location.origin}/install-guide`} size={84} />
              </div>
              <p className="text-xs text-[#52525B] m-0">Want it on your <b>phone</b>? Point your phone's camera at this code — it opens the install guide with the right steps for that device.</p>
            </div>
          )}
          <a href="/install-guide" target="_blank" rel="noreferrer" data-testid="install-guide-link"
            className="text-xs font-bold text-[#3F6212] no-underline hover:underline">
            Full step-by-step guide for every device (shareable) →
          </a>
        </DialogContent>
      </Dialog>
      {showBreadcrumb && <Breadcrumb nav={nav} />}
    </header>
  );
}
